import React, { useEffect, useMemo, useRef } from "react";
import {
  PanResponder,
  Platform,
  TouchableOpacity,
  View,
  type ViewStyle,
} from "react-native";

const SWIPE_THRESHOLD = 16;

type Props = {
  isExpanded: boolean;
  onExpand: () => void;
  onCollapse: () => void;
  onToggle?: () => void;
  handleStyle: ViewStyle;
  children?: React.ReactNode;
  style?: ViewStyle;
};

function getDomNode(ref: View | null): HTMLElement | null {
  if (!ref) return null;
  const candidate = ref as unknown as { _node?: HTMLElement };
  if (candidate instanceof HTMLElement) return candidate;
  if (candidate._node instanceof HTMLElement) return candidate._node;
  return null;
}

function getPointerClientY(event: MouseEvent | TouchEvent): number {
  if ("touches" in event && event.touches.length > 0) {
    return event.touches[0].clientY;
  }
  if ("changedTouches" in event && event.changedTouches.length > 0) {
    return event.changedTouches[0].clientY;
  }
  return (event as MouseEvent).clientY;
}

export default function GradientExpandSwipeArea({
  isExpanded,
  onExpand,
  onCollapse,
  onToggle,
  handleStyle,
  children,
  style,
}: Props) {
  const containerRef = useRef<View>(null);
  const onExpandRef = useRef(onExpand);
  const onCollapseRef = useRef(onCollapse);
  const didSwipeRef = useRef(false);
  const trackingRef = useRef(false);
  const startYRef = useRef(0);

  onExpandRef.current = onExpand;
  onCollapseRef.current = onCollapse;

  const applySwipe = (dy: number) => {
    if (dy <= -SWIPE_THRESHOLD) {
      didSwipeRef.current = true;
      onExpandRef.current();
      return;
    }
    if (dy >= SWIPE_THRESHOLD) {
      didSwipeRef.current = true;
      onCollapseRef.current();
    }
  };

  const panResponder = useMemo(
    () =>
      PanResponder.create({
        onStartShouldSetPanResponder: () => false,
        onMoveShouldSetPanResponder: (_, gestureState) =>
          Math.abs(gestureState.dy) > Math.abs(gestureState.dx) &&
          Math.abs(gestureState.dy) > 6,
        onPanResponderTerminationRequest: () => false,
        onPanResponderRelease: (_, gestureState) => {
          applySwipe(gestureState.dy);
        },
      }),
    [],
  );

  useEffect(() => {
    if (Platform.OS !== "web") return;

    let cleanup: (() => void) | undefined;
    let raf2 = 0;

    const setup = () => {
      const domNode = getDomNode(containerRef.current);
      if (!domNode) return false;

      const onStart = (event: MouseEvent | TouchEvent) => {
        startYRef.current = getPointerClientY(event);
        trackingRef.current = true;
        didSwipeRef.current = false;
      };

      const onEnd = (event: MouseEvent | TouchEvent) => {
        if (!trackingRef.current) return;
        trackingRef.current = false;
        const dy = getPointerClientY(event) - startYRef.current;
        applySwipe(dy);
      };

      const onCancel = () => {
        trackingRef.current = false;
      };

      domNode.addEventListener("mousedown", onStart);
      domNode.addEventListener("touchstart", onStart, { passive: true });
      document.addEventListener("mouseup", onEnd);
      document.addEventListener("touchend", onEnd, { passive: true });
      document.addEventListener("touchcancel", onCancel, { passive: true });

      cleanup = () => {
        domNode.removeEventListener("mousedown", onStart);
        domNode.removeEventListener("touchstart", onStart);
        document.removeEventListener("mouseup", onEnd);
        document.removeEventListener("touchend", onEnd);
        document.removeEventListener("touchcancel", onCancel);
      };
      return true;
    };

    const raf1 = requestAnimationFrame(() => {
      if (!setup()) {
        raf2 = requestAnimationFrame(() => {
          setup();
        });
      }
    });

    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      cleanup?.();
    };
  }, []);

  const handleToggle = () => {
    if (didSwipeRef.current) {
      didSwipeRef.current = false;
      return;
    }
    if (onToggle) {
      onToggle();
      return;
    }
    if (isExpanded) {
      onCollapse();
    } else {
      onExpand();
    }
  };

  const nativeHandlers =
    Platform.OS === "web" ? {} : panResponder.panHandlers;

  return (
    <View
      ref={containerRef}
      style={[
        style,
        Platform.OS === "web" &&
          ({
            touchAction: "none",
            userSelect: "none",
            WebkitUserSelect: "none",
            cursor: "grab",
          } as unknown as ViewStyle),
      ]}
      {...nativeHandlers}
    >
      <TouchableOpacity
        style={{ padding: 24 }}
        onPress={handleToggle}
        activeOpacity={0.8}
      >
        <View style={handleStyle} />
      </TouchableOpacity>
      {children}
    </View>
  );
}
